import { ReactNode } from 'react'

/**
 * A single switch in the layer panel. Named `LayerToggleOption` rather than `LayerToggle` so it
 * does not collide with the `LayerToggle` component of the same name.
 */
export type LayerToggleOption = {
  value: boolean
  setValue: (value: boolean) => void
  name: string | ReactNode
  childrenOptions?: LayerToggleOption[]
}

/**
 * The panel's toggles, keyed by name. Insertion order is render order.
 *
 * `traffic`, `satellite` and `tasks` have purpose-built components behind them, so their entries
 * may be a bare `boolean` — `true` renders the component uncontrolled (it owns its own state),
 * `false` hides it. Any other key must supply a full `LayerToggleOption` to render at all.
 */
export type LayerToggles = {
  traffic?: boolean | LayerToggleOption
  satellite?: boolean | LayerToggleOption
  tasks?: boolean | LayerToggleOption
  // Unfortunately, typescript doesn't support hybrid types https://github.com/microsoft/TypeScript/issues/17867
  [key: string]: boolean | LayerToggleOption | undefined
}

/** The state of the "Vehicle Labels" sub-section, owned by the calling page. */
export type VehicleLabelsConfig = {
  showRegistration: boolean
  showShareReference: boolean
  setShowRegistration: (value: boolean) => void
  setShowShareReference: (value: boolean) => void
  clearVehicleLabels: () => void
  /**
   * Tag labels are opt-in from the call site — omit `setShowTags` and the toggle is not rendered
   * at all, which is how the map keeps it behind the `assetTags` flag.
   */
  showTags?: boolean
  setShowTags?: (value: boolean) => void
}

export type LayerManagerProps = {
  toggles?: LayerToggles
  vehicleLabels?: VehicleLabelsConfig
}

/** Keys that resolve to a purpose-built toggle component instead of the generic one. */
export const SPECIAL_TOGGLE_KEYS = ['traffic', 'satellite', 'tasks'] as const

export type SpecialToggleKey = (typeof SPECIAL_TOGGLE_KEYS)[number]

export type ResolvedToggleKind = SpecialToggleKey | 'generic'

/** One entry of the panel's render list, produced by `resolveLayerToggles`. */
export type ResolvedLayerToggle = {
  key: string
  kind: ResolvedToggleKind
  /**
   * The caller's toggle state, or `undefined` when the toggle should manage its own state.
   * Always present when `kind` is `'generic'`.
   */
  option?: LayerToggleOption
}
